/**
 * The visitor's local stations -> lineup channels. Stations come from channels-service through the
 * existing `/api/local-channels`; `packageNames` mixes plan names with billing codes, and a local joins
 * exactly the authored packages whose `package_id` appears there. Import-light: the ZIP control runs it.
 */

import type { RawStation } from "../../directv-design-system/services/zip/local-rsn-channels";
import { MYHOME_TEAM_CODE } from "../../directv-design-system/organisms/channel-lineup/myhome-team.shared";
import type { LineupChannel as ModelChannel } from "./lineup-model";

/** Beam's plan name for MyHome Team. Beam sends plan names, not billing codes, for RSNs. */
const BEAM_MYHOME_TEAM_PLAN = "MYHOMETEAM";

/** What the browser needs to place a local: the lineup's authored package codes. */
export interface LocalStationRules {
  /** Every authored `package_id` on the blok, signature and Genre alike. */
  codes: string[];
}

/** One authored entry's `package_id`, trimmed; the resolver and the locals rules read it the same way. */
export function authoredPackageCodeOf(entry: unknown): string | undefined {
  const code = (entry as { package_id?: unknown } | null | undefined)?.package_id;
  return typeof code === "string" && code.trim() !== "" ? code.trim() : undefined;
}

/** Built on the server from the blok's own `packages[]`, then handed to the ZIP control. */
export function localStationRulesFor(packages: unknown): LocalStationRules {
  const codes: string[] = [];
  for (const entry of Array.isArray(packages) ? packages : packages ? [packages] : []) {
    const code = authoredPackageCodeOf(entry);
    if (code && !codes.includes(code)) {
      codes.push(code);
    }
  }
  return { codes };
}

/** The MyHome Team code, when this blok authors that pack. */
function myHomeTeamCodeIn(rules: LocalStationRules | undefined): string | undefined {
  return rules?.codes?.includes?.(MYHOME_TEAM_CODE) ? MYHOME_TEAM_CODE : undefined;
}

function textOf(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
}

function namesOf(value: unknown): Set<string> {
  return new Set(Array.isArray(value) ? value.filter((name): name is string => typeof name === "string") : []);
}

/** One row per station that names at least one authored package; the rest stay off the page. */
export function localChannelsFrom(
  stations: readonly RawStation[] | null | undefined,
  rules: LocalStationRules | undefined
): ModelChannel[] {
  const rows: ModelChannel[] = [];
  if (!rules) {
    return rows;
  }
  const ids = new Map<string, number>();
  for (const station of stations ?? []) {
    const names = namesOf(station?.packageNames);
    const packages: ModelChannel["packages"] = {};
    for (const code of rules.codes ?? []) {
      if (names.has(code)) {
        packages[code] = "included";
      }
    }
    if (Object.keys(packages).length === 0) {
      continue;
    }
    const name = textOf(station?.network) || textOf(station?.name);
    const callSign = textOf(station?.callSign) || undefined;
    const number = textOf(station?.channelNumber) || undefined;
    // An SD/HD pair can share call sign and number, so a repeat gets a counter.
    const base = `local:${callSign ?? name}:${number ?? ""}`;
    const seen = (ids.get(base) ?? 0) + 1;
    ids.set(base, seen);
    rows.push({
      id: seen === 1 ? base : `${base}:${seen}`,
      name,
      number,
      callSign,
      logoUrl: textOf(station?.logo) || undefined,
      categories: [],
      kind: "local",
      myFree: station?.myFree === true,
      fastChannel: station?.fastChannel === true,
      packages
    });
  }
  return rows;
}

/** The Genre codes an in-market RSN joins: MyHome Team, when beam lists it. */
export function rsnGenreCodes(
  rsn: { beamPackageNames?: unknown },
  rules: LocalStationRules | undefined
): string[] {
  const code = myHomeTeamCodeIn(rules);
  if (!code) {
    return [];
  }
  // Beam text is untyped on the wire; a non-string entry is skipped, never trimmed.
  const names = new Set(
    (Array.isArray(rsn?.beamPackageNames) ? rsn.beamPackageNames : [])
      .filter((name): name is string => typeof name === "string")
      .map(name => name.trim().toUpperCase())
  );
  return names.has(BEAM_MYHOME_TEAM_PLAN) || names.has(code.toUpperCase()) ? [code] : [];
}

/** A local in MyHome Team gives way to a listed RSN of the same name, so the network shows once. */
export function withoutMyHomeTeamDuplicates<T extends { name: string; packages?: Record<string, unknown> }>(
  locals: readonly T[],
  rsns: readonly { name: string }[],
  rules: LocalStationRules | undefined
): T[] {
  const code = myHomeTeamCodeIn(rules);
  if (!code || rsns.length === 0) {
    return [...locals];
  }
  const rsnNames = new Set(rsns.map(rsn => textOf(rsn?.name).toLowerCase()).filter(name => name !== ""));
  return locals.filter(local => !(local.packages?.[code] && rsnNames.has(textOf(local?.name).toLowerCase())));
}
